import { Button, Grid, Group, Modal, NumberInput, SegmentedControl, Switch, TagsInput, Text } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import EventSelector from '../EventSelector';

const LoyaltyGrantModal = ({ loyaltyGrant: passedLoyaltyGrant, onClose, onSave, currency }) => {
  const currentDomain = useSelector((state) => state.domain.domain);
  const fetch = useFetch();

  const [loyaltyGrant, setLoyaltyGrant] = useState(passedLoyaltyGrant);
  const [userError, setUserError] = useState();

  useEffect(() => {
    setLoyaltyGrant(passedLoyaltyGrant);
  }, [passedLoyaltyGrant]);

  if (loyaltyGrant === undefined) {
    return '';
  }

  const saveLoyaltyGrant = (e) => {
    e.preventDefault();
    setUserError(undefined);

    const fetchFunc = loyaltyGrant?.id
      ? fetch(
          `/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}/loyalty/grants/${loyaltyGrant.id}`,
          {
            method: 'PUT',
            body: JSON.stringify({ loyaltyGrant }),
          }
        )
      : fetch(`/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}/loyalty/grants`, {
          method: 'POST',
          body: JSON.stringify({ loyaltyGrant }),
        });

    fetchFunc
      .then(({ loyaltyGrant }) => {
        onSave(loyaltyGrant);
        onClose();
      })
      .catch(setUserError);
  };

  return (
    <Modal
      opened={!!passedLoyaltyGrant}
      onClose={onClose}
      title={`${loyaltyGrant?.id ? 'Edit' : 'Add'} Loyalty Grant Rule`}
    >
      <form>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Event*</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <EventSelector
              actions={[loyaltyGrant.matchEvent]}
              setActions={([matchEvent]) => setLoyaltyGrant({ ...loyaltyGrant, matchEvent })}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Tags</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TagsInput
              clearable
              data={[]}
              value={loyaltyGrant.matchTags || []}
              onChange={(matchTags) => setLoyaltyGrant({ ...loyaltyGrant, matchTags })}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Type*</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={loyaltyGrant.type ?? ''}
              onChange={(type) => setLoyaltyGrant({ ...loyaltyGrant, type })}
              data={[
                { label: 'Value-based', value: 'valueBased' },
                { label: 'Event-based', value: 'eventBased' },
              ]}
            />
            <Text size="xs" c="dimmed" mt="xs">
              Value-based means that the number of points depends on the value of the given action (for ex. the total
              cost of purchase).
              <br />
              Event-based means that a fixed number of points are granted each time a user performs the given action.
            </Text>
          </Grid.Col>
        </Grid>

        {loyaltyGrant.type === 'valueBased' && (
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Min Value</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <NumberInput
                name="minValue"
                min={0}
                step={0.01}
                decimalScale={2}
                value={loyaltyGrant.minValue || 0}
                onChange={(value) => setLoyaltyGrant({ ...loyaltyGrant, minValue: value })}
                rightSection={
                  <Text size="sm" c="dimmed" pr="xs">
                    {currentDomain.currency}
                  </Text>
                }
                rightSectionWidth={50}
              />
            </Grid.Col>
          </Grid>
        )}

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Expiry*</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={loyaltyGrant.expiry ?? ''}
              onChange={(expiry) => setLoyaltyGrant({ ...loyaltyGrant, expiry })}
              data={[
                { label: 'Calendar', value: 'calendar' },
                { label: 'Duration', value: 'duration' },
              ]}
            />
          </Grid.Col>
        </Grid>

        {loyaltyGrant?.expiry === 'duration' && (
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Expire After*</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <NumberInput
                name="validDays"
                min={1}
                step={1}
                value={loyaltyGrant.validDays || 0}
                error={!loyaltyGrant.validDays}
                onChange={(value) => setLoyaltyGrant({ ...loyaltyGrant, validDays: value })}
                rightSection={
                  <Text size="sm" c="dimmed" pr="xs">
                    days
                  </Text>
                }
                rightSectionWidth={50}
              />
            </Grid.Col>
          </Grid>
        )}

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Points per 1 {loyaltyGrant.type === 'valueBased' ? currency : 'event'}*</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <NumberInput
              name="grantAmount"
              min={1}
              step={1}
              value={loyaltyGrant.grantAmount || 0}
              error={!loyaltyGrant.grantAmount}
              onChange={(value) => setLoyaltyGrant({ ...loyaltyGrant, grantAmount: value })}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label htmlFor="reversibleOnRefund">Reverse on refund</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Switch
              id="reversibleOnRefund"
              checked={loyaltyGrant.reversibleOnRefund !== false}
              onChange={(e) => setLoyaltyGrant({ ...loyaltyGrant, reversibleOnRefund: e.currentTarget.checked })}
            />
            <Text size="xs" c="dimmed" mt="xs">
              When on, refunding an order that earned these points subtracts them back, proportionally to the refunded
              amount. Turn off for grants that should be kept even when the order is refunded (e.g. a fixed promo bonus).
            </Text>
          </Grid.Col>
        </Grid>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!loyaltyGrant?.grantAmount || !loyaltyGrant?.expiry || !loyaltyGrant?.matchEvent}
            onClick={saveLoyaltyGrant}
            leftSection={<IconDeviceFloppy size={16} />}
          >
            Save
          </Button>
        </Group>
        {userError && <ErrorMessage message={userError.message} />}
      </form>
    </Modal>
  );
};

export default LoyaltyGrantModal;
